import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { queryClient } from './lib/query-client';
import { AuthProvider } from './lib/auth';
import { AuthGuard } from './components/app/AuthGuard';
import { RequireRole } from './components/app/RequireRole';
import { AppLayout } from './components/app/AppLayout';
import { ErrorBoundary } from './components/app/ErrorBoundary';
import LoginPage from './routes/login';
import VerifyEmailPage from './routes/verify-email';
import ForgotPasswordPage from './routes/forgot-password';
import ResetPasswordPage from './routes/reset-password';
import SetPasswordPage from './routes/set-password';
import ChangePasswordPage from './routes/change-password';
import ApiKeysPage from './routes/api-keys';
import OverviewPage from './routes/overview';
import LicensesPage from './routes/licenses';
import NewLicensePage from './routes/licenses/new';
import LicenseDetailPage from './routes/licenses/$id';
import BlacklistPage from './routes/blacklist';
import AuditPage from './routes/audit';
import RequestsPage from './routes/requests';
import RequestDetailPage from './routes/requests/$id';
import OperatorsPage from './routes/operators';
import FactoryUsbPage from './routes/factory-usb';
import FingerprintRequestsPage from './routes/fingerprint-requests';
import NotFoundPage from './routes/not-found';

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route
              element={
                <ErrorBoundary>
                  <AuthGuard>
                    <AppLayout />
                  </AuthGuard>
                </ErrorBoundary>
              }
            >
              <Route
                path="/"
                element={
                  <RequireRole capability="viewOverview">
                    <OverviewPage />
                  </RequireRole>
                }
              />
              <Route
                path="/licenses"
                element={
                  <RequireRole capability="manageLicenses">
                    <LicensesPage />
                  </RequireRole>
                }
              />
              <Route
                path="/licenses/new"
                element={
                  <RequireRole capability="manageLicenses">
                    <NewLicensePage />
                  </RequireRole>
                }
              />
              <Route
                path="/licenses/:id"
                element={
                  <RequireRole capability="manageLicenses">
                    <LicenseDetailPage />
                  </RequireRole>
                }
              />
              <Route
                path="/blacklist"
                element={
                  <RequireRole capability="viewBlacklist">
                    <BlacklistPage />
                  </RequireRole>
                }
              />
              <Route
                path="/audit"
                element={
                  <RequireRole capability="viewAudit">
                    <AuditPage />
                  </RequireRole>
                }
              />
              <Route
                path="/requests"
                element={
                  <RequireRole capability={['submitLicenseRequests', 'reviewLicenseRequests']}>
                    <RequestsPage />
                  </RequireRole>
                }
              />
              <Route
                path="/requests/:id"
                element={
                  <RequireRole capability="submitLicenseRequests">
                    <RequestDetailPage />
                  </RequireRole>
                }
              />
              <Route
                path="/operators"
                element={
                  <RequireRole capability="manageUsers">
                    <OperatorsPage />
                  </RequireRole>
                }
              />
              <Route
                path="/factory-usb"
                element={
                  <RequireRole capability="manageLicenses">
                    <FactoryUsbPage />
                  </RequireRole>
                }
              />
              <Route path="/api-keys" element={<ApiKeysPage />} />
              <Route
                path="/fingerprint-requests"
                element={
                  <RequireRole capability="manageUsers">
                    <FingerprintRequestsPage />
                  </RequireRole>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </QueryClientProvider>
  );
}
